/**
 * molecule.dev-specific chat stream events.
 *
 * The shared chat UI (`@molecule/app-ide-react` ChatPanel) renders only generic
 * events. App-specific events the backend emits as `{ type: 'custom', name, data }`
 * are registered here so they surface as chat cards — keeping molecule.dev specifics
 * OUT of the shared packages (see molecule AGENTS.md anti-pattern 14: don't bake
 * consumer-specific events/cards into core packages).
 */
import { getClient } from '@molecule/app-http'
import { t } from '@molecule/app-i18n'
import { registerCustomEventCard } from '@molecule/app-ide-react'
import { error as logError } from '@molecule/app-logger'

import { authClient } from './bonds/auth-default.js'
import { FEEDBACK_URL } from './branding.js'
import { getKeepAwake, isWakeLockSupported, setKeepAwake } from './lib/workspaceSettings.js'
import { emitNotification } from './notifications.js'
import {
  allowanceResetLine,
  balanceEmptyLine,
  billingCta,
  type BillingCtaAction,
  githubRepoLinkLabel,
  honestLimitNote,
  MOLECULE_REPO_URL,
  ownerBalanceEmptyLine,
  ownerSpendCapReachedLine,
  spendCapReachedLine,
  upgradeLabel,
} from './upgrade-copy.js'
import { sandboxErrorCopy } from './utilities/sandbox-error-copy.js'

/**
 * `data-mol-id` of the shared `@molecule/app-ide-react` ChatPanel header's
 * bug-report button — the trigger for molecule.dev's in-product ReportModal
 * (mvp SYN10). The modal's open-state lives inside that shared package, so this
 * stable `data-mol-id` (per DESIGN.md's "all interactive elements carry a
 * data-mol-id for programmatic interaction" contract) is how molecule.dev opens
 * it from outside the package without coupling the package to molecule.dev.
 */
export const REPORT_BUTTON_MOL_ID = 'chat-report-button'

/**
 * `data-mol-id` of the Workspace toolbar's Environment button — opens the
 * merged Environment modal on its Setup tab. Used by the `setup_report` card
 * to deep-link "add your API keys" without coupling the shared ChatPanel to
 * molecule.dev's panel state (same decoupled-trigger pattern as
 * {@link REPORT_BUTTON_MOL_ID}).
 */
export const ENVIRONMENT_BUTTON_MOL_ID = 'topbar-environment'

/**
 * Window events bridging the chat `import_repo` card (GIT2) to the Workspace,
 * which owns the ImportRepoModal + the chat resume send. Same decoupling goal
 * as the data-mol-id triggers: the card factory stays free of Workspace state.
 */
export const IMPORT_REPO_OPEN_EVENT = 'mol:import-repo-open'

/** Window event a card dispatches to open the workspace's Autoruns panel. */
export const AUTORUNS_OPEN_EVENT = 'mol:autoruns-open'
export const IMPORT_REPO_SKIP_EVENT = 'mol:import-repo-skip'

/**
 * sessionStorage key the ImportRepoModal sets (to the projectId) right before
 * navigating away for the GitHub/GitLab OAuth consent. When the provider
 * redirects back to `/workspace/:projectId?gitAuth=…&gitStatus=connected`, the
 * Workspace sees the flag and reopens the import modal so the user lands back
 * exactly where they left — mid-import, now connected. Deploy-panel connects
 * never set it, so they only get the status notice.
 */
export const IMPORT_REPO_CONNECT_RETURN_KEY = 'mol:import-repo-connect-return'

/**
 * Open molecule.dev's in-product report flow (mvp SYN10): the ReportModal that
 * collects a title/description/steps and auto-attaches the recent chat + project
 * context, then files a GitHub issue + DB row. Typing `/report` and the header
 * bug-report button both open it; the first-run pre-alpha card's "/report" action
 * (mvp L6) routes here too, INSTEAD of bouncing the user to the raw external
 * issues page (which bypassed the auto-attached context entirely).
 *
 * The modal's open-state lives inside the shared ChatPanel, so we trigger it the
 * sanctioned, decoupled way — clicking the header's `data-mol-id` bug-report
 * button (identical to typing `/report`). If that button isn't mounted (e.g. the
 * conversation header is hidden), we fall back to opening the public issue tracker
 * so the report channel always works.
 */
export function openInProductReport(): void {
  const button = document.querySelector<HTMLElement>(`[data-mol-id="${REPORT_BUTTON_MOL_ID}"]`)
  if (button) {
    button.click()
    return
  }
  // The in-product trigger isn't on screen — keep the report channel working by
  // opening the public open-source issue tracker in a new tab.
  window.open(FEEDBACK_URL, '_blank', 'noopener,noreferrer')
}

/**
 * Run a saved script from the "Saved script — Run" card (mvp SYN1) and surface the
 * outcome as a notification banner.
 *
 * The card is emitted by molecule-dev/api when Synthase saves a script via the
 * `save_script` tool. Its Run button calls the SAME run endpoint the `/scripts`
 * panel and `/run <name>` use (`POST /projects/:id/scripts/:name/run`), via the
 * shared HTTP client, so a one-click run works without typing a command. Result
 * feedback rides the lightweight `emitNotification` channel (a styled banner) —
 * the shared ChatPanel owns the in-chat output card for `/run`, which this card
 * stays decoupled from; for the full stdout/stderr the user can use `/run {name}`
 * or the `/scripts` panel.
 *
 * @param projectId - The project the script belongs to.
 * @param name - The saved script's name.
 */
async function runSavedScriptFromCard(projectId: string, name: string): Promise<void> {
  emitNotification(
    t('chat.scriptSaved.running', { name }, { defaultValue: `Running ${name}…` }),
    'info',
  )
  try {
    const res = await getClient().post<{ stdout?: string; stderr?: string; exitCode?: number }>(
      `/projects/${encodeURIComponent(projectId)}/scripts/${encodeURIComponent(name)}/run`,
    )
    const exitCode = typeof res.data?.exitCode === 'number' ? res.data.exitCode : 0
    if (exitCode === 0) {
      emitNotification(
        t('chat.scriptSaved.ranOk', { name }, { defaultValue: `${name} finished (exit 0).` }),
        'info',
      )
    } else {
      emitNotification(
        t(
          'chat.scriptSaved.ranFail',
          { name, code: exitCode },
          { defaultValue: `${name} exited with code ${exitCode}.` },
        ),
        'warning',
      )
    }
  } catch (error) {
    logError('Failed to run saved script from card', error)
    emitNotification(
      t('chat.scriptSaved.runError', { name }, { defaultValue: `Could not run ${name}.` }),
      'error',
    )
  }
}

// The caller's edit capability on the ACTIVE project, pushed by Workspace when
// the project (or its role) loads. Card factories are registered once at
// startup with no React context, so mutating actions (egress Allow, script
// Run) consult this at render time — a viewer reading the shared transcript
// must not be offered buttons that can only 403 (the endpoints are editor+).
// Defaults true: solo/owner is the overwhelmingly common case, and a wrong
// true only yields the pre-existing error toast.
let chatCardCanEdit = true

/** Push the active project's edit capability for the custom-card actions. */
export function setChatCardCanEdit(canEdit: boolean): void {
  chatCardCanEdit = canEdit
}

/**
 * Approve an egress destination for a project — the "Allow" action on the
 * blocked-connection card. Adds the host to `settings.egressAllowlist` (the
 * same policy the egress proxy consults), so the agent's next attempt to reach
 * it succeeds. See docs/sandbox-egress-enforcement.md (workspace root).
 *
 * @param projectId - The project whose sandbox was blocked.
 * @param host - The destination hostname the sandbox tried to reach.
 */
async function allowEgressHostFromCard(projectId: string, host: string): Promise<void> {
  try {
    await getClient().post(`/projects/${encodeURIComponent(projectId)}/egress/allowlist`, { host })
    emitNotification(
      t('chat.egressDenial.allowed', { host }, { defaultValue: `${host} allowed.` }),
      'info',
    )
  } catch (error) {
    logError('Failed to allow egress host from card', error)
    emitNotification(
      t('chat.egressDenial.allowError', { host }, { defaultValue: `Could not allow ${host}.` }),
      'error',
    )
  }
}

/**
 * Format US cents as dollars for the `/cost` card — two decimals, with a
 * fraction of a cent shown as `<$0.01` rather than a misleading `$0.00`.
 *
 * @param cents - The amount in US cents (fractional ok).
 * @returns e.g. `$1.24`, `<$0.01`, `$0.00`.
 */
export function formatCents(cents: number): string {
  if (!Number.isFinite(cents) || cents <= 0) return '$0.00'
  if (cents < 0.5) return '<$0.01'
  return `$${(cents / 100).toFixed(2)}`
}

/**
 * Download the project's eject bundle — the "Download bundle" action on the
 * `/eject` card. Fetches the SAME `GET /projects/:id/export` the Settings
 * surface uses (the bundle is built on demand, admin+), through the shared
 * HTTP client so the session credential rides along (a plain `<a href>` to the
 * API would not carry it), then hands the blob to the browser as a file.
 *
 * @param projectId - The project to export.
 * @param slug - The project slug, for the file name.
 */
async function downloadEjectBundle(projectId: string, slug: string): Promise<void> {
  emitNotification(
    t('chat.eject.building', undefined, { defaultValue: 'Building your export bundle…' }),
    'info',
  )
  try {
    const res = await getClient().get<Blob>(`/projects/${encodeURIComponent(projectId)}/export`, {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(res.data)
    const link = document.createElement('a')
    link.href = url
    link.download = `molecule-eject-${slug}.tar.gz`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  } catch (error) {
    logError('Failed to download eject bundle from card', error)
    // A blob-typed request parses even the ERROR body as a Blob: read the
    // server's sentence back so a state refusal (asleep, the running-project
    // limit) is said as itself, not as "could not build".
    const reason = await blobErrorSentence(
      (error as { response?: { data?: unknown } } | null)?.response?.data,
    )
    // A sandbox-state refusal was already toasted (once, with its action) by
    // the global interceptor; only the generic failure is said here.
    if (reason) return
    emitNotification(
      t('chat.eject.downloadError', undefined, {
        defaultValue: 'Could not build the export bundle.',
      }),
      'error',
    )
  }
}

/**
 * The IDE copy for an error body that came back as a Blob, or null when it is
 * not one / not JSON / carries no known key or sentence.
 *
 * @param data - The error response body.
 * @returns The sentence, or null.
 */
async function blobErrorSentence(data: unknown): Promise<string | null> {
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return null
  try {
    const parsed = JSON.parse(await data.text()) as { errorKey?: unknown; error?: unknown }
    const key = typeof parsed.errorKey === 'string' ? parsed.errorKey : null
    return sandboxErrorCopy(key, t) ?? (typeof parsed.error === 'string' ? parsed.error : null)
  } catch (_error) {
    // Not JSON — nothing better than the generic line to say.
    return null
  }
}

/**
 * Register all molecule.dev custom chat-event cards. Call once at startup (main.tsx).
 */
export function registerChatCustomEvents(): void {
  // Blocked outbound connection: the sandbox tried to reach a host that is not on
  // the project's egress allowlist, and the egress proxy denied it (the backend
  // emits { type:'custom', name:'egress_denial', data:{ projectId, host, port } }).
  // The user is the only one who can decide a destination is legitimate, so the
  // card offers a one-click Allow. Copy is two short beats — what happened + the
  // choice (rule 13); the agent has already been told, in its own tool error, to
  // ask rather than retry.
  registerCustomEventCard('egress_denial', (data) => {
    const host = typeof data?.host === 'string' ? data.host : ''
    if (!host) return null
    const projectId = typeof data?.projectId === 'string' ? data.projectId : ''
    const port = typeof data?.port === 'number' ? data.port : 0
    const target = port ? `${host}:${port}` : host
    return {
      text: t(
        'chat.egressDenial.text',
        { target },
        {
          defaultValue: `Blocked an outbound connection to ${target}. Allow it only if you expected this.`,
        },
      ),
      // Amber "needs your attention" treatment — same tip-card look as the other notices.
      tone: 'upgrade',
      // A blocked connection is a critical event: it keeps its timestamp with timestamps off.
      critical: true,
      // Allow is editor+ server-side — no button for viewers reading along.
      action:
        projectId && chatCardCanEdit
          ? [
              {
                label: t('chat.egressDenial.allow', { host }, { defaultValue: `Allow ${host}` }),
                onClick: () => void allowEgressHostFromCard(projectId, host),
              },
            ]
          : undefined,
    }
  })

  // Preview unreachable: a preview tool (read/interact/navigate) could not reach any
  // browser — the backend emits { type:'custom', name:'preview_unreachable',
  // data:{ projectId, reason: 'no_browser' | 'no_reply' } } once per turn. This is not a
  // platform fault: driving the live preview needs the user's tab open and screen on (a
  // locked/backgrounded page is frozen by the OS). Say so plainly, and offer the one-click
  // fix — enabling the per-device "Keep screen awake" preference — when it would help.
  registerCustomEventCard('preview_unreachable', (data) => {
    const reason = data?.reason === 'no_browser' ? 'no_browser' : 'no_reply'
    const text =
      reason === 'no_browser'
        ? t('chat.previewUnreachable.noBrowser', undefined, {
            defaultValue:
              'Synthase could not drive the live preview because no browser was connected. Keep this tab open and your screen awake during builds for live preview checks.',
          })
        : t('chat.previewUnreachable.noReply', undefined, {
            defaultValue:
              'The live preview did not respond to Synthase. If this tab was hidden or your screen was off, keep it open and awake so Synthase can drive the preview.',
          })
    const offerKeepAwake = isWakeLockSupported() && !getKeepAwake()
    return {
      text,
      tone: 'info',
      critical: true,
      action: offerKeepAwake
        ? [
            {
              label: t('chat.previewUnreachable.keepAwake', undefined, {
                defaultValue: 'Keep screen awake',
              }),
              onClick: () => setKeepAwake(true),
            },
          ]
        : undefined,
    }
  })

  // Saved-script confirmation (mvp SYN1): when Synthase saves a reusable script via
  // the `save_script` tool, the backend emits { type: 'custom', name: 'script_saved',
  // data: { projectId, name, description } }. Surface it as an emphasized "Saved
  // script — Run" card so the user can run it in one click (no terminal / remembered
  // syntax — the whole point of /scripts), or still use /run <name> or the /scripts
  // panel. The save_script tool + this card live OUT of the shared @molecule/app-ide-react
  // package (molecule AGENTS.md #14) — only molecule.dev knows its own run endpoint + copy.
  registerCustomEventCard('script_saved', (data) => {
    const name = typeof data?.name === 'string' ? data.name : ''
    if (!name) return null
    const projectId = typeof data?.projectId === 'string' ? data.projectId : ''
    const description = typeof data?.description === 'string' ? data.description.trim() : ''
    const text = description
      ? t(
          'chat.scriptSaved.withDescription',
          { name, description },
          { defaultValue: `Saved script: ${name} — ${description}` },
        )
      : t('chat.scriptSaved.text', { name }, { defaultValue: `Saved script: ${name}` })
    return {
      text,
      // Completed-action tip (green + check) — same consistent tip-card structure.
      tone: 'success',
      // Only offer a Run button when we know the project (we always do here); the
      // run goes through the same endpoint as /scripts and /run <name> — which
      // is editor+ (it executes in the sandbox), so no button for viewers.
      action:
        projectId && chatCardCanEdit
          ? [
              {
                label: t('chat.scriptSaved.run', undefined, { defaultValue: 'Run' }),
                onClick: () => void runSavedScriptFromCard(projectId, name),
              },
            ]
          : undefined,
    }
  })

  // Scheduled / idle chat tasks (the schedule_task tool): the backend emits
  // { kind: 'custom', name: 'task_scheduled', data: { taskId, projectId, prompt,
  // mode, runAt } }. Surface what was queued — the prompt verbatim (it runs as a
  // fresh turn later, so the user should see exactly what will execute) — plus a
  // one-click Cancel until it fires. Editor-gated like every action that changes
  // what the agent does (a viewer can read, not cancel).
  registerCustomEventCard('task_scheduled', (data) => {
    const taskId = typeof data?.taskId === 'string' ? data.taskId : ''
    const projectId = typeof data?.projectId === 'string' ? data.projectId : ''
    const prompt = typeof data?.prompt === 'string' ? data.prompt.trim() : ''
    if (!taskId || !projectId) return null
    const mode = data?.mode === 'idle' ? 'idle' : 'scheduled'
    const runAt = typeof data?.runAt === 'string' ? data.runAt : null
    const when =
      mode === 'idle'
        ? t('chat.taskScheduled.whenIdle', undefined, {
            defaultValue: 'when the budget has headroom',
          })
        : runAt
          ? t(
              'chat.taskScheduled.at',
              { time: new Date(runAt).toLocaleString() },
              {
                defaultValue: 'at {{time}}',
              },
            )
          : ''
    return {
      text: t(
        'chat.taskScheduled.text',
        { prompt, when },
        {
          defaultValue:
            when.trim().length > 0 ? `Scheduled ${when}: “${prompt}”` : `Scheduled: “${prompt}”`,
        },
      ),
      tone: 'info',
      action: chatCardCanEdit
        ? [
            {
              label: t('chat.taskScheduled.cancel', undefined, { defaultValue: 'Cancel task' }),
              color: 'secondary',
              onClick: () => {
                void (async () => {
                  try {
                    await getClient().delete(
                      `/projects/${encodeURIComponent(projectId)}/scheduled-tasks/${encodeURIComponent(taskId)}`,
                    )
                    emitNotification(
                      t('chat.taskScheduled.cancelled', undefined, {
                        defaultValue: 'Task cancelled.',
                      }),
                      'info',
                    )
                  } catch (_error) {
                    // The toast below IS the handling — the task may simply
                    // have already run (404) or the network dropped.
                    emitNotification(
                      t('chat.taskScheduled.cancelFailed', undefined, {
                        defaultValue: 'Could not cancel the task — it may have already run.',
                      }),
                      'error',
                    )
                  }
                })()
              },
            },
          ]
        : undefined,
    }
  })

  // Deploy (DEPLOY1): the /deploy command triggered a production deploy and the
  // backend emitted a `card` frame carrying { kind: 'custom', name: 'deploy_result',
  // data: { ok, started, deploymentId | error } }. The deploy runs in the background — this card just
  // confirms it started (or why it couldn't); the user watches progress in the
  // Pipeline panel. One plain sentence (rule 13).
  registerCustomEventCard('deploy_result', (data) => {
    if (data?.ok === true) {
      return {
        text: t('chat.deployResult.started', undefined, {
          defaultValue: 'Deploy started. Watch it build and go live in the Pipeline panel.',
        }),
        tone: 'info',
      }
    }
    const error = typeof data?.error === 'string' && data.error ? data.error : ''
    return {
      text: error
        ? t(
            'chat.deployResult.failed',
            { error },
            { defaultValue: `Could not start the deploy: ${error}` },
          )
        : t('chat.deployResult.failedGeneric', undefined, {
            defaultValue: 'Could not start the deploy.',
          }),
      tone: 'info',
      critical: true,
    }
  })

  // Repo import (GIT2): Synthase's request_repo_import tool paused the turn and
  // the backend emitted { type: 'custom', name: 'import_repo', data: { reason } }.
  // The card's actions hand off to the Workspace via window events — it opens the
  // ImportRepoModal (connect provider → pick repo / paste URL) or resumes the
  // pending tool with a skip. Copy is one plain sentence (rule 13).
  // Scheduled-task lifecycle. `task_started` (from the scheduler dispatch): a
  // one-line marker that the queued task is now running — its prompt follows as
  // an automatic message. `tasks_result` (from the /tasks command): the
  // project's task list, one line per task with status + trigger.
  registerCustomEventCard('task_started', (data) => {
    const prompt = typeof data?.prompt === 'string' ? data.prompt.trim() : ''
    return {
      text: t(
        'chat.taskStarted.text',
        { prompt },
        {
          defaultValue: `Scheduled task started: “${prompt}”`,
        },
      ),
      tone: 'info',
    }
  })

  // Badge status — the /badge command's answer (api/src/ai/commands/badge-command.ts):
  // whether the next deploy of THIS app carries the "Built with Molecule.dev"
  // badge and which rule decided it. A toggle attempt by a FREE owner never
  // reaches this card — the server emits the registered `upgrade_prompt` whose
  // View-plans action opens the plans modal instead.
  registerCustomEventCard('badge_status', (data) => {
    if (data?.ok !== true) {
      const usage = typeof data?.usage === 'string' ? data.usage : ''
      const error = typeof data?.error === 'string' ? data.error : ''
      return {
        text:
          usage ||
          error ||
          t('chat.badgeStatus.failed', undefined, {
            defaultValue: 'Could not read the badge setting.',
          }),
        tone: 'info',
      }
    }
    const summary = typeof data?.summary === 'string' ? data.summary : ''
    const note = typeof data?.note === 'string' ? data.note : ''
    const parts = [summary, note].filter(Boolean)
    return {
      text: '',
      content: [
        t('chat.badgeStatus.header', undefined, { defaultValue: 'Built with Molecule.dev badge:' }),
        ...parts,
      ],
      tone: 'info',
    }
  })

  registerCustomEventCard('tasks_result', (data) => {
    if (data?.ok !== true) {
      return {
        text:
          typeof data?.error === 'string' && data.error
            ? data.error
            : t('chat.tasksResult.failed', undefined, { defaultValue: 'Failed to list tasks.' }),
        tone: 'upgrade',
      }
    }
    const tasks = Array.isArray(data?.tasks) ? data.tasks : []
    if (tasks.length === 0) {
      return {
        text: t('chat.tasksResult.empty', undefined, {
          defaultValue:
            'No scheduled tasks. Ask Synthase to schedule one, or use the schedule_task tool.',
        }),
        tone: 'info',
      }
    }
    const content = tasks.slice(0, 10).map((raw) => {
      const task = (raw ?? {}) as {
        prompt?: unknown
        status?: unknown
        mode?: unknown
        runAt?: unknown
        lastError?: unknown
      }
      const prompt = typeof task.prompt === 'string' ? task.prompt : ''
      const status = typeof task.status === 'string' ? task.status : '?'
      const when =
        task.mode === 'idle'
          ? 'idle'
          : typeof task.runAt === 'string' && task.runAt
            ? new Date(task.runAt).toLocaleString()
            : ''
      const line = `${status.toUpperCase()}${when ? ` · ${when}` : ''} — ${prompt}`
      return typeof task.lastError === 'string' && task.lastError
        ? `${line} (last defer: ${task.lastError})`
        : line
    })
    return {
      text: '',
      // The system card renders `content` segments; the header rides first.
      content: [
        t('chat.tasksResult.header', undefined, { defaultValue: 'Scheduled tasks:' }),
        ...content,
      ],
      tone: 'info',
    }
  })

  registerCustomEventCard('import_repo', (data) => {
    const reason = typeof data?.reason === 'string' ? data.reason.trim() : ''
    return {
      text:
        reason ||
        t('chat.importRepo.text', undefined, {
          defaultValue: 'Connect your repository so Synthase can work on your actual code.',
        }),
      tone: 'info',
      // Choosing a repo (POST git-import-source) and the Skip resume (a chat
      // send) are both editor+ — no buttons for viewers reading along.
      action: chatCardCanEdit
        ? [
            {
              label: t('chat.importRepo.choose', undefined, { defaultValue: 'Choose repository' }),
              onClick: () => window.dispatchEvent(new CustomEvent(IMPORT_REPO_OPEN_EVENT)),
            },
            {
              label: t('chat.importRepo.skip', undefined, { defaultValue: 'Skip' }),
              onClick: () => window.dispatchEvent(new CustomEvent(IMPORT_REPO_SKIP_EVENT)),
            },
          ]
        : undefined,
    }
  })

  // Integration setup report: after a build that leaves required API keys missing,
  // the backend emits { type: 'custom', name: 'setup_report', data: { integrations:
  // [{ package, label, keys, setupUrl }], missingRequired } } (fingerprint-deduped
  // server-side so an unchanged missing set isn't re-nagged). The card is one plain
  // sentence — no env-var dumps, no integration list (rule 13; exact wording per the
  // user, 2026-07-06) — and only renders when at least one required key is actually
  // missing. The Environment panel it deep-links to lists each key + where to get it.
  registerCustomEventCard('setup_report', (data) => {
    const integrations = Array.isArray(data?.integrations)
      ? (data.integrations as Array<{ package?: string; label?: string; keys?: string[] }>)
      : []
    const hasMissingKeys = integrations.some((i) => Array.isArray(i.keys) && i.keys.length > 0)
    if (!hasMissingKeys) return null
    return {
      text: t('chat.setupReport.text', undefined, {
        defaultValue:
          'Most of the app should already work in the preview, but some integrations still need to be configured by you.',
      }),
      tone: 'info',
      icon: 'key',
      // The Environment panel (and its toolbar button) is editor+ — for a
      // viewer the button it clicks doesn't exist, so offer nothing.
      action: chatCardCanEdit
        ? [
            {
              label: t('chat.setupReport.open', undefined, { defaultValue: 'Add API keys' }),
              onClick: () => {
                const button = document.querySelector<HTMLElement>(
                  `[data-mol-id="${ENVIRONMENT_BUTTON_MOL_ID}"]`,
                )
                button?.click()
              },
            },
          ]
        : undefined,
    }
  })

  // Build degraded on the free tier — an honest notice (the message comes from the
  // backend) that the cheap free-tier model can struggle with demanding builds that
  // stronger paid models handle better, plus an Upgrade affordance. Emitted by
  // molecule-dev/api as { type: 'custom', name: 'build_degraded', data: { message } }.
  registerCustomEventCard('build_degraded', (data) => {
    const message = typeof data?.message === 'string' ? data.message : ''
    if (!message) return null
    // CST3: pair the upgrade affordance with the honest line (free + open source;
    // unlimited inference isn't sustainable, so there are limits) + a repo link.
    return {
      text: `${message}\n\n${honestLimitNote()}`,
      action: [
        // Server-gated to the free tier, so Upgrade is always the right next
        // step here and there is no billing state to resolve. It still uses the
        // shared Upgrade label — `upgrade.viewPlans` resolves to "View plans",
        // which is not what this button means.
        { label: upgradeLabel(), href: '/pricing', color: 'primary' },
        { label: githubRepoLinkLabel(), href: MOLECULE_REPO_URL, color: 'secondary' },
      ],
      // Upgrade-nudge tip (amber + clock) — one consistent tip-card look across notices.
      tone: 'upgrade',
    }
  })

  // Upgrade prompt — emitted by molecule-dev/api when a user hits a plan cap (e.g. the
  // max-loops setting exceeds their tier). The dynamic message (with the actual cap
  // numbers) comes from the backend; molecule.dev owns the Upgrade route (/pricing) and
  // the action copy here, behind t() — the shared chat package knows none of it. Emitted
  // as { type: 'custom', name: 'upgrade_prompt', data: { feature?, message } }.
  registerCustomEventCard('upgrade_prompt', (data) => {
    const message = typeof data?.message === 'string' ? data.message : ''
    // The SERVER decides what actually fixes this limit and ships it as
    // `billingAction`; the resolver below turns it into the same button every
    // other surface shows for the same fact. A card recorded BEFORE the field
    // shipped carries none, so the resolver derives one from `feature`.
    const billingAction = typeof data?.billingAction === 'string' ? data.billingAction : undefined
    // An explicit null means there is no tier above this one — a Team account at
    // a plan cap gets no button rather than a plans modal with nothing to sell.
    const upgradeTier =
      data?.upgradeTier === null
        ? null
        : typeof data?.upgradeTier === 'string'
          ? data.upgradeTier
          : undefined
    const feature = typeof data?.feature === 'string' ? data.feature : undefined
    // Spend-cap pause (docs/BAAS-pricing.md §3): `feature: 'spend_cap'` means the
    // user's OWN monthly extra-usage cap was reached — the trust rail worked as
    // designed. Render it ACTIONABLE (raise your cap in Billing), never the
    // upgrade/reset framing below: no honest-upgrade note, no /pricing action.
    // Rendered even without a backend message (local fallback line) so the pause
    // is never silent.
    //
    // Empty prepaid balance (`usage_balance`) is the same actionable shape with a
    // different fix: the user is already paying, so it is never an upgrade pitch.
    // Which fix exactly — add funds, add a card, or contact support because the
    // top-up velocity rail refuses more — is the server's call, not a guess from
    // the feature name.
    if (feature === 'spend_cap' || feature === 'usage_balance') {
      // A teammate cannot change the owner's billing: the server's "raise it in
      // Billing" sentence plus "Only the project owner can change billing" told
      // them two opposite things. One sentence that names whose cap it is.
      if (billingAction === 'owner_only') {
        return {
          text: feature === 'spend_cap' ? ownerSpendCapReachedLine() : ownerBalanceEmptyLine(),
          tone: 'info',
          critical: true,
          coversLimitType: feature,
        }
      }
      const cta = billingCta({ billingAction, limitType: feature, upgradeTier })
      const lead = message || (feature === 'spend_cap' ? spendCapReachedLine() : balanceEmptyLine())
      return {
        // `owner_only` gives this viewer no button, so the note IS the answer.
        text: cta.note ? `${lead}\n\n${cta.note}` : lead,
        ...(cta.actions ? { action: cta.actions } : {}),
        tone: 'info',
        // Every upgrade_prompt reports a limit that stopped the work — critical.
        critical: true,
        // The next send is refused with the same `limitType`, whose live banner
        // repeats this card verbatim — declare the coverage so only one shows.
        coversLimitType: feature,
      }
    }
    if (!message) return null
    // CST4: when the backend tags the per-user AI-budget interrupt with the next
    // reset (`resetAt`), LEAD with "refreshes in ~Xh / in about 12 days" — the
    // retention rationale ("so users keep coming back") — instead of the
    // Upgrade-first message. Other upgrade_prompt features (e.g. max_loops) and
    // the platform-capacity case carry no resetAt and keep their backend message.
    const resetAt = typeof data?.resetAt === 'number' && data.resetAt > 0 ? data.resetAt : undefined
    // Carry the tier so the reset line names the allowance ("…budget for guests…"),
    // matching the backend message it replaces on the resetAt path.
    const currentTier = typeof data?.currentTier === 'string' ? data.currentTier : undefined
    // …and the WINDOW, so a paid tier's monthly allowance is not announced as
    // "today's free AI budget". Absent on an older API → the daily framing.
    const allowanceWindow =
      data?.allowanceWindow === 'billing-period' || data?.allowanceWindow === 'daily'
        ? data.allowanceWindow
        : undefined
    const lead =
      resetAt != null
        ? allowanceResetLine(resetAt, undefined, currentTier, allowanceWindow)
        : message
    // A guest ("…budget for guests…", tier 'anonymous') can't act on Upgrade without an
    // account, and signing up / logging in ALSO preserves their work + lifts the limit —
    // so lead with Sign up / Log in for guests (same routes + copy as the guest_reminder
    // card), then the Upgrade + repo links. Logged-in tiers keep just Upgrade + repo.
    const isGuest = currentTier === 'anonymous'
    // A GUEST allowance card is stale the moment the viewer is signed in: their
    // account has a different budget and the Sign up / Log in CTAs are dead ends.
    // The claim flow also drops these server-side; this covers the live in-place
    // transcript (and any card the claim couldn't reach).
    if (isGuest && authClient.isAuthenticated()) return null
    // A guest can't Upgrade without an account, and signing up / logging in is
    // what actually lifts their cap (a real account has a bigger free budget) —
    // so guests get Sign up + Log in and NO Upgrade. Everyone else gets whatever
    // the server said fixes it: Upgrade for a plan cap with a tier above it,
    // "Enable extra usage" for a paid account that never opted in, and NO button
    // at all for the top tier (`upgradeTier: null`) or a teammate who cannot
    // change the owner's billing.
    const cta = billingCta({ billingAction, limitType: feature, isGuest, upgradeTier })
    const ctaActions: BillingCtaAction[] = cta.actions ?? []
    // CST3: the honest line + repo link, same as build_degraded — never a dark pattern.
    return {
      text: cta.note
        ? `${lead}\n\n${cta.note}\n\n${honestLimitNote()}`
        : `${lead}\n\n${honestLimitNote()}`,
      action: [
        ...ctaActions,
        { label: githubRepoLinkLabel(), href: MOLECULE_REPO_URL, color: 'secondary' },
      ],
      // Plan-cap upgrade nudge — same upgrade tip-card treatment as build_degraded.
      tone: 'upgrade',
      critical: true,
      // The daily-budget card and the 429 the NEXT send gets are the SAME limit hit:
      // the card says "…budget for guests. It resets tomorrow…" and the live banner
      // says "…budget for guests. It resets in about 23 hours, or you can log in for
      // more free AI." Declare the coverage so the card steps aside while that
      // banner is up (it returns when the error clears). Only the daily-budget card
      // covers it — a max_loops / too-large-turn prompt is a different fact.
      ...(resetAt != null && data?.feature === 'ai_cost' ? { coversLimitType: 'ai_cost' } : {}),
    }
  })

  // Guest reminder — emitted by molecule-dev/api for anonymous users (never during
  // discovery) as { type: 'custom', name: 'guest_reminder', data: { message?, expiresInHours? } }.
  // molecule.dev owns the copy and its own /signup // /login routes, all behind t(); the
  // shared chat package has no auth routes. `expiresInHours` defaults to the guest-session
  // TTL so the "expires after N hours" copy stays in sync with the backend.
  registerCustomEventCard('guest_reminder', (data) => {
    // A signed-in viewer has already done what this card asks — hide it (the
    // claim flow also drops it server-side; this covers the live transcript).
    if (authClient.isAuthenticated()) return null
    const hours = typeof data?.expiresInHours === 'number' ? data.expiresInHours : 72
    const message =
      typeof data?.message === 'string' && data.message
        ? data.message
        : t('guest.reminder.message', undefined, {
            defaultValue: `Sign up or log in to keep your work. Guest sessions expire after ${hours} hours.`,
          })
    return {
      text: message,
      action: [
        {
          label: t('guest.reminder.signUp', undefined, { defaultValue: 'Sign up' }),
          href: '/signup',
          color: 'primary',
        },
        {
          label: t('guest.reminder.logIn', undefined, { defaultValue: 'Log in' }),
          href: '/login',
          color: 'success',
        },
      ],
      // Auth nudge — primary accent + sign-in icon (same tip-card structure).
      tone: 'signup',
    }
  })

  // First working build, guest only — emitted once per project by molecule-dev/api the
  // first time an anonymous builder's app compiles and boots (verify=ok) as
  // { type: 'custom', name: 'first_build_signup', data: { projectId } }. The ambient
  // GuestReminder shows regardless of build state; this is the ask tied to the moment
  // the guest has just seen the product work.
  registerCustomEventCard('first_build_signup', () => {
    if (authClient.isAuthenticated()) return null
    return {
      text: t('guest.firstBuild.message', undefined, {
        defaultValue: 'Your app is running. Sign up to save it and deploy it live.',
      }),
      action: [
        {
          label: t('guest.reminder.signUp', undefined, { defaultValue: 'Sign up' }),
          href: '/signup',
          color: 'primary',
        },
      ],
      tone: 'signup',
    }
  })

  // Logged-in welcome — recorded server-side by the guest-claim flow (in place of
  // the dropped guest nudges) as { type: 'custom', name: 'logged_in',
  // data: { name?, email? } }. One friendly line confirming the session + that the
  // guest work now belongs to the account.
  registerCustomEventCard('logged_in', (data) => {
    const name = typeof data?.name === 'string' && data.name.trim() ? data.name.trim() : ''
    const email = typeof data?.email === 'string' && data.email.trim() ? data.email.trim() : ''
    const who = name || email
    return {
      text: who
        ? t(
            'auth.chat.loggedInAs',
            { who },
            {
              defaultValue: `Welcome, ${who} — you're logged in and your work is saved to your account.`,
            },
          )
        : t('auth.chat.loggedIn', undefined, {
            defaultValue: "Welcome — you're logged in and your work is saved to your account.",
          }),
      // Confirmation — green check, same tip-card treatment as other notices.
      tone: 'success',
    }
  })

  // First-run model transparency (mvp L5): on a free user's first prompt in a
  // conversation, before the model starts, name the models powering Synthase AND state
  // the limitation honestly — the free executor is a fast, low-cost model that can make
  // mistakes and sometimes needs guidance — with an inline upgrade link. NOT the old
  // upside-only soft-pedal. FREE TIER ONLY — molecule-dev/api emits this event solely for
  // free/anonymous users (chat-handler.ts onboardingNoticeFor gated on isFreeTier), so
  // upgraded users never see it. The plan/execute model display names ride in the event
  // `data` (resolved from the free-tier clamp), with the fixed free pair as fallback.
  // Copy is i18n'd HERE (defaultValue is the real text), not in the API — the backend
  // stays generic. Rendered as a gold tip card (tone: 'gold') using composable inline
  // `content` segments — prose, then the 'upgrade options' link (→ /pricing), then the
  // trailing period — so the link sits inline mid-sentence, not only at the end. `text`
  // holds the plain-text equivalent for accessibility. The pre-alpha card (prealpha_notice
  // below) shares this exact gold-tip treatment so both onboarding beats look unified.
  registerCustomEventCard('models_intro', (data) => {
    const planModel =
      typeof data?.planModel === 'string' && data.planModel ? data.planModel : 'Claude Sonnet'
    const executeModel =
      typeof data?.executeModel === 'string' && data.executeModel
        ? data.executeModel
        : 'DeepSeek V4 Flash'
    // Prose up to and including the space before the inline upgrade link (ends "…see our ").
    // Names both free-tier models, positively framed — never definitively limiting about
    // the free tier: these handle most apps; the advanced models are for more complex
    // requirements. (Exact copy per the user, 2026-07-06.)
    const prefix = t(
      'chat.onboarding.modelsIntro',
      { planModel, executeModel },
      {
        defaultValue:
          `For free users, Synthase plans with ${planModel} and executes with ${executeModel}. ` +
          `The Molecule architecture allows smaller models like these to handle most apps, ` +
          `but if you have more complex requirements, see our `,
      },
    )
    const upgradeLabel = t('upgrade.optionsLink', undefined, { defaultValue: 'upgrade options' })
    // Trailing prose AFTER the inline upgrade link.
    const suffix = t('chat.onboarding.modelsIntroAfterLink', undefined, {
      defaultValue: ' for more advanced AI models.',
    })
    return {
      tone: 'gold',
      // Plain-text equivalent (accessibility / non-toned consumers).
      text: `${prefix}${upgradeLabel}${suffix}`,
      // Composable inline body: prose → inline upgrade link → trailing prose.
      content: [prefix, { label: upgradeLabel, href: '/pricing' }, suffix],
    }
  })

  // Pre-alpha disclaimer (mvp L6): on the user's second prompt, after the first
  // substantive turn, an honest one-liner that Molecule is pre-alpha (rough edges,
  // bugs) plus the /report nudge. Emitted as { type: 'custom', name: 'prealpha_notice' }.
  // Trimmed to two plain sentences (rule 13; exact wording per the user, 2026-07-06) —
  // the value-prop sentence and the secondary issue-tracker link were cut.
  //
  // L6 — surface /report + route to the in-product ReportModal: the card NAMES the
  // `/report` command the user explicitly asked for and makes its action open the
  // in-product ReportModal (mvp SYN10) — which auto-attaches the recent chat + project
  // context — via openInProductReport(), NOT the raw external GitHub issues page (that
  // bypass dropped all the auto-attached context).
  //
  // UNIFIED TREATMENT (mvp L5): both first-run onboarding cards (this one + models_intro)
  // share the same visual language — a gold (`tone: 'gold'`) dismissable tip box with a
  // lightbulb glyph and any actions rendered as inline underlined links via composable
  // `content` segments, NOT a standalone primary button. Keep this `tone` + the
  // content-segment shape in lockstep with models_intro so the two onboarding beats
  // don't render in two different styles.
  registerCustomEventCard('prealpha_notice', () => {
    const disclaimer = t('chat.onboarding.preAlpha', undefined, {
      defaultValue: "Molecule is pre-alpha — you'll hit rough edges and bugs.",
    })
    // Nudge that NAMES the /report command (L6) — prose up to the inline "/report" token.
    const nudgePrefix = t('chat.onboarding.reportNudgePrefix', undefined, {
      defaultValue: ' Type ',
    })
    // The literal command, rendered as an inline link that opens the in-product modal.
    const reportCommandLabel = t('chat.onboarding.reportCommand', undefined, {
      defaultValue: '/report',
    })
    // Trailing prose AFTER the /report token.
    const nudgeSuffix = t('chat.onboarding.reportNudgeSuffix', undefined, {
      defaultValue: " to send some feedback and we'll handle it ASAP.",
    })
    return {
      tone: 'gold',
      // Plain-text equivalent (accessibility / non-toned consumers) — also names /report.
      text: `${disclaimer}${nudgePrefix}${reportCommandLabel}${nudgeSuffix}`,
      // Composable inline body: disclaimer prose → /report (opens the in-product
      // ReportModal, SYN10) → trailing prose.
      content: [
        disclaimer,
        nudgePrefix,
        // Render the command as inline monospace code so it stands out (mvp #10).
        { label: reportCommandLabel, code: true, onClick: openInProductReport },
        nudgeSuffix,
      ],
    }
  })

  // /invite result (SOC2) — molecule-dev/api intercepts the `/invite <email> [role]`
  // chat command, runs the SAME validated team-invite flow as the REST endpoint
  // (owner/admin RBAC + collaborator tier limit), and emits the outcome as a `card`
  // frame carrying { kind: 'custom', name: 'team_invite_result', data: {...} }. The card renders
  // the confirmation / error here (copy + i18n live in molecule.dev, not the shared
  // package — molecule AGENTS.md #14). When the failure is the plan/limit gate, we
  // pair it with the same honest Upgrade affordance the other cards use.
  registerCustomEventCard('team_invite_result', (data) => {
    if (data?.usage) {
      return {
        text: t('chat.invite.usage', undefined, {
          defaultValue: 'Usage: /invite <email> [admin | editor | viewer] — invite a collaborator.',
        }),
      }
    }
    if (data?.ok) {
      const email = typeof data.email === 'string' ? data.email : ''
      const role = typeof data.role === 'string' ? data.role : 'editor'
      // Say what actually happened. The card used to promise an email on a path
      // that sent none; now it reports the send, and says so plainly when the
      // send failed (the invitee can still accept from their dashboard).
      const text =
        data.emailSent === false
          ? t(
              'chat.invite.invitedNoEmail',
              { email, role },
              {
                defaultValue: `Invited ${email} as ${role}. We could not send the email — they can accept from their dashboard.`,
              },
            )
          : t(
              'chat.invite.invited',
              { email, role },
              {
                defaultValue: `Invited ${email} as ${role}. They have an email with a link to join.`,
              },
            )
      return { text, emphasized: true }
    }
    const error =
      typeof data?.error === 'string' && data.error
        ? data.error
        : t('chat.invite.failed', undefined, { defaultValue: 'Could not send that invite.' })
    // The server says which failures the user can act on (`upgrade` /
    // `requiresSignup`). It used to be inferred by regex over the message, which
    // missed the commonest plan message of all — the one the CTA exists for.
    const isPlanGated = data?.upgrade === true
    const needsSignup = data?.requiresSignup === true
    return {
      text: isPlanGated ? `${error}\n\n${honestLimitNote()}` : error,
      emphasized: isPlanGated || needsSignup,
      action: isPlanGated
        ? [
            {
              // `upgrade.viewPlans` resolves to "View plans"; this CTA means
              // Upgrade, so it uses the key that says so.
              label: upgradeLabel(),
              href: '/pricing',
              color: 'primary' as const,
            },
            { label: githubRepoLinkLabel(), href: MOLECULE_REPO_URL, color: 'secondary' as const },
          ]
        : needsSignup
          ? [
              {
                label: t('chat.invite.signUp', undefined, { defaultValue: 'Sign up' }),
                href: '/signup',
                color: 'primary' as const,
              },
            ]
          : undefined,
    }
  })

  // /teamsay (SOC2) — the human-only side channel. A posted note is now a
  // `teamOnly` MESSAGE (a `{ type: 'message' }` stream event rendered by the
  // shared ChatPanel as a gold-accented user-style message), NOT a card — this
  // factory remains for the usage hint, the command-failed card, and legacy
  // team_say card-messages persisted before the message-based redesign
  // (2026-08-27), which still reload from their cardEvent.
  registerCustomEventCard('team_say', (data) => {
    if (data?.usage) {
      return {
        text: t('chat.teamSay.usage', undefined, {
          defaultValue:
            'Usage: /teamsay <message> (or /t) — message your team without involving Synthase.',
        }),
      }
    }
    // The server's command-failed card ({ ok: false, error }) — without this
    // branch a failed /teamsay rendered nothing at all.
    if (typeof data?.error === 'string' && data.error) {
      return { text: data.error }
    }
    const author = typeof data?.author === 'string' && data.author ? data.author : 'A teammate'
    const text = typeof data?.text === 'string' ? data.text : ''
    if (!text) return null
    return {
      text: t(
        'chat.teamSay.posted',
        { author, text },
        { defaultValue: `${author} → team chat (Synthase won't reply): ${text}` },
      ),
      emphasized: true,
    }
  })

  // Standard role denial for a server-intercepted command (/hooks add|remove,
  // /eject): the server resolved the caller's team role at the source and
  // refused. Same copy as the shared ChatPanel's read-only note, so a denial
  // reads identically whichever side caught it. Emitted as
  // { kind:'custom', name:'command_denied', data:{ command, requiredRole } }.
  registerCustomEventCard('command_denied', (data) => {
    const requiredRole = typeof data?.requiredRole === 'string' ? data.requiredRole : 'editor'
    return {
      text:
        requiredRole === 'admin' || requiredRole === 'owner'
          ? t('chat.commandDenied.admin', undefined, {
              defaultValue:
                'This command needs admin access on the project. Ask the owner or an admin.',
            })
          : t('ide.chat.viewerReadOnlyCommand', undefined, {
              defaultValue:
                'You have view-only access, so this command is unavailable. Ask an editor to make changes.',
            }),
      critical: true,
    }
  })

  // No `/cost` card here: `/cost` stays the shared @molecule/app-ide-react
  // unitless meter (owner decision 2026-09-04).

  // /hooks — the project's hooks and autoruns. The server reads and writes the
  // SAME files Synthase loads each turn (.synthase/hooks.json +
  // .agents/settings.json) through the store the Autoruns panel uses. Data:
  // list → { ok, action:'list', hooks:[{id,source,event,name?,matcher,type,
  // command,steps?,prompt?,enabled}], builtIns:[{name,event,enabled,canDisable}],
  // lastRuns:{name→run}, defaults (older servers), invalidFiles };
  // add/remove → { ok, action, hook, file }; enable/disable → { ok, action,
  // name, enabled, builtIn }; run → { ok, action:'run', run }; otherwise
  // { ok:false, usage, reason } or { ok:false, error }. Every card offers
  // "Open Autoruns" — the panel is where they are managed with buttons.
  registerCustomEventCard('hooks_result', (data) => {
    const openPanel = [
      {
        label: t('chat.hooks.openPanel', undefined, { defaultValue: 'Open Autoruns' }),
        onClick: () => window.dispatchEvent(new CustomEvent(AUTORUNS_OPEN_EVENT)),
      },
    ]
    const usageHint = t('chat.hooks.usage', undefined, {
      defaultValue:
        'Usage: /hooks — list; /hooks add <tool-regex> <command>; /hooks add <Event> <command | check <steps> | agent <task>> (Event: PostDeploy, TaskEnd, Stop, FileChanged); /hooks enable|disable|remove|run <# or name>.',
    })
    if (data?.usage) return { text: usageHint }
    if (data?.ok !== true) {
      const error = typeof data?.error === 'string' && data.error ? data.error : ''
      return {
        text:
          error ||
          t('chat.hooks.failed', undefined, { defaultValue: 'Could not update the hooks.' }),
        critical: true,
      }
    }
    type Hook = {
      id: number
      source?: string
      event?: string
      name?: string
      matcher: string
      type?: string
      command: string
      steps?: string[]
      prompt?: string
      enabled?: boolean
    }
    const does = (hook: Hook): string =>
      hook.type === 'check'
        ? `check ${hook.steps?.length ? hook.steps.join(', ') : 'all'}`
        : hook.type === 'agent'
          ? `agent: ${(hook.prompt ?? '').slice(0, 60)}`
          : hook.command
    const describe = (hook: Hook): string => {
      const label = hook.name ? `${hook.name}: ` : ''
      const where = hook.source === 'settings' ? ' (.agents/settings.json)' : ''
      const event = hook.event && hook.event !== 'PreToolUse' ? `${hook.event} ` : ''
      const off = hook.enabled === false ? ' (off)' : ''
      return `#${hook.id} ${label}${event}${hook.matcher || '*'} → ${does(hook)}${where}${off}`
    }
    if (data.action === 'add' || data.action === 'remove') {
      const hook = data.hook as Hook
      const file = typeof data.file === 'string' ? data.file : ''
      const line = describe(hook)
      const isAutorun = hook.event !== undefined && !/ToolUse$/.test(hook.event)
      return {
        text:
          data.action === 'add'
            ? isAutorun
              ? t(
                  'chat.hooks.addedAutorun',
                  { line, file },
                  { defaultValue: `Added autorun ${line} to ${file}.` },
                )
              : t(
                  'chat.hooks.added',
                  { line, file },
                  {
                    defaultValue: `Added hook ${line} to ${file}. It runs before matching tool calls; a non-zero exit asks you to confirm.`,
                  },
                )
            : t(
                'chat.hooks.removed',
                { line, file },
                { defaultValue: `Removed hook ${line} from ${file}.` },
              ),
        tone: 'success',
        action: openPanel,
      }
    }
    if (data.action === 'enable' || data.action === 'disable') {
      const name = typeof data.name === 'string' ? data.name : ''
      return {
        text:
          data.enabled === true
            ? t('chat.hooks.enabled', { name }, { defaultValue: `${name} is on.` })
            : t('chat.hooks.disabled', { name }, { defaultValue: `${name} is off.` }),
        tone: 'success',
        action: openPanel,
      }
    }
    if (data.action === 'run') {
      const run = (data.run ?? {}) as {
        name?: string
        status?: string
        durationMs?: number
        output?: string
      }
      const seconds = Math.max(1, Math.round((run.durationMs ?? 0) / 1000))
      const name = run.name ?? ''
      const verdict =
        run.status === 'passed'
          ? t(
              'chat.hooks.runPassed',
              { name, seconds },
              { defaultValue: `${name} passed (${seconds}s).` },
            )
          : run.status === 'failed'
            ? t(
                'chat.hooks.runFailed',
                { name, seconds },
                { defaultValue: `${name} failed (${seconds}s).` },
              )
            : t('chat.hooks.runError', { name }, { defaultValue: `${name} could not run.` })
      const output =
        typeof run.output === 'string' ? run.output.trim().split('\n').slice(-8).join('\n') : ''
      return {
        text: output ? `${verdict}\n${output}` : verdict,
        ...(run.status === 'passed' ? { tone: 'success' as const } : { critical: true }),
        action: openPanel,
      }
    }
    const hooks = Array.isArray(data.hooks) ? (data.hooks as Hook[]) : []
    const builtIns = Array.isArray(data.builtIns)
      ? (data.builtIns as Array<{ name: string; enabled?: boolean }>)
      : Array.isArray(data.defaults)
        ? (data.defaults as Array<{ name: string }>)
        : []
    const lastRuns = (data.lastRuns ?? {}) as Record<string, { status?: string }>
    const builtInText = builtIns
      .map((b) => {
        const off = (b as { enabled?: boolean }).enabled === false ? ' (off)' : ''
        const last = lastRuns[b.name]?.status ? ` — last run ${lastRuns[b.name].status}` : ''
        return `${b.name}${off}${last}`
      })
      .join(', ')
    const invalid = Array.isArray(data.invalidFiles) ? (data.invalidFiles as string[]) : []
    const lines: string[] = []
    if (hooks.length === 0) {
      lines.push(
        t(
          'chat.hooks.noneYet',
          { builtIns: builtInText },
          { defaultValue: `No project hooks yet. Built-in: ${builtInText}.` },
        ),
      )
    } else {
      lines.push(
        t('chat.hooks.listHeader', undefined, {
          defaultValue: 'Project hooks (remove with /hooks remove <#>):',
        }),
        ...hooks.map((h) => {
          const last =
            h.name && lastRuns[h.name]?.status ? ` — last run ${lastRuns[h.name].status}` : ''
          return describe(h) + last
        }),
        t(
          'chat.hooks.builtIn',
          { defaults: builtInText },
          { defaultValue: `Built-in: ${builtInText}.` },
        ),
      )
    }
    lines.push(
      t('chat.hooks.addEventHint', undefined, {
        defaultValue:
          'Add one: /hooks add Stop check typecheck lint — or /hooks add <tool-regex> <command>',
      }),
    )
    for (const file of invalid) {
      lines.push(
        t(
          'chat.hooks.invalidFile',
          { file },
          { defaultValue: `${file} is not valid JSON and was skipped.` },
        ),
      )
    }
    return { text: lines.join('\n'), tone: 'info', action: openPanel }
  })

  // /eject — no lock-in. The server verified the caller may export (admin+, the
  // export route's own gate) and emitted { ok, projectId, slug, sandboxRunning }.
  // The Download action builds + fetches the bundle (code, DB dump, .env,
  // EJECTED.md) via GET /projects/:id/export; the text carries the two-line
  // run-it-standalone instruction (rule 13: short, plain, no pitch).
  registerCustomEventCard('eject_result', (data) => {
    if (data?.ok !== true) {
      const error = typeof data?.error === 'string' && data.error ? data.error : ''
      return {
        text:
          error ||
          t('chat.eject.failed', undefined, {
            defaultValue: 'Could not prepare the export right now.',
          }),
        critical: true,
      }
    }
    const projectId = typeof data.projectId === 'string' ? data.projectId : ''
    const slug = typeof data.slug === 'string' && data.slug ? data.slug : 'project'
    const ready =
      data.sandboxRunning === false
        ? t('chat.eject.readyNoSandbox', undefined, {
            defaultValue:
              'Your export bundle is ready: database dump and .env, with nothing tied to molecule.dev. Start the sandbox first if you want the code included.',
          })
        : t('chat.eject.ready', undefined, {
            defaultValue:
              'Your export bundle is ready: code, database dump and .env, with nothing tied to molecule.dev.',
          })
    const standalone = t('chat.eject.standalone', undefined, {
      defaultValue:
        'Run it standalone: unpack code.tar.gz, then npm install && npm run dev.\nEJECTED.md inside has the exact steps (env, database, production).',
    })
    return {
      text: `${ready}\n${standalone}`,
      tone: 'success',
      icon: 'download',
      action: projectId
        ? [
            {
              label: t('chat.eject.download', undefined, { defaultValue: 'Download bundle' }),
              onClick: () => void downloadEjectBundle(projectId, slug),
              color: 'primary',
            },
          ]
        : undefined,
    }
  })
}
